'use client';

import { createLogger } from '@ctrl-enter/tools/utils';
import * as React from 'react';
import { PERMISSION_STATUS } from '../services/electron/constants';
import { useElectron } from '../services/electron/electron-provider';
import { useScreenCapture } from '../services/electron/hooks/use-screen-capture';
import { useScreenCapturePermission } from '../stores/system-settings-store';
import { cn } from '../utils/cn';
import { Button } from './button';
import { Dialog } from './dialog';
import { IconButton } from './icon-button';

const logger = createLogger('client:component:screen-capture-permission-modal');

export type ScreenCapturePermissionModalProps = {
  onClose: () => void;
  className?: string;
};

export const ScreenCapturePermissionModal: React.FC<ScreenCapturePermissionModalProps> = ({
  onClose,
  className,
}) => {
  const permission = useScreenCapturePermission();
  const electronService = useElectron();
  const [isRequesting, setIsRequesting] = React.useState(false);
  const screenCapture = useScreenCapture();

  // Web version: Screen capture is handled by extension
  React.useEffect(() => {
    // No-op in web - extension handles screen capture
    // Could trigger extension capture here if needed
  }, []);

  React.useEffect(() => {
    logger.info('MODAL - NEXT PERMISSION', permission);
    // In web, we only check for GRANTED status (extension grants permission)
    if (permission === PERMISSION_STATUS.GRANTED) {
      setIsRequesting(false);
      onClose();
    }
  }, [permission, onClose]);

  const handleOpenSystemPreferences = async () => {
    setIsRequesting(true);
    try {
      await electronService.screenCaptureControls.openSystemSettings();
    } catch (error) {
      logger.error('Failed to open system preferences:', error);
      setIsRequesting(false);
    }
  };
  const platform = navigator.platform.toLowerCase();
  const userAgent = navigator.userAgent.toLowerCase();
  const isMac = platform.includes('mac') || userAgent.includes('mac');
  const isWindows = platform.includes('win') || userAgent.includes('windows');

  const settingsName = isMac ? 'System Preferences' : isWindows ? 'Settings' : 'System Settings';

  return (
    <Dialog
      isOpen={true}
      onClose={onClose}
      size="md"
      aria-labelledby="screen-capture-title"
      className={cn('p-6', className)}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-orange-100 dark:bg-orange-900/20 flex items-center justify-center">
            <svg
              className="w-5 h-5 text-orange-600 dark:text-orange-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
          <h2 id="screen-capture-title" className="text-lg font-semibold text-foreground">
            Screen Recording Permission Required
          </h2>
        </div>
        <IconButton iconName="X" variant="ghost" size="sm" onClick={onClose} aria-label="Close" />
      </div>

      {/* Content */}
      <div className="mb-6">
        <p className="text-sm text-muted-foreground leading-relaxed mb-4">
          This app needs permission to capture your screen to create screenshots for AI analysis.
          Please grant permission in {settingsName}.
        </p>

        <div className="bg-muted/50 rounded-md p-3 text-xs text-muted-foreground">
          <strong>Note:</strong> Screen recording permission is required to capture screenshots
          that are sent to AI for analysis. Your screen content is only captured when you
          explicitly create a new chat or take a screenshot.
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-3 justify-end">
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>

        <Button onClick={handleOpenSystemPreferences} disabled={isRequesting}>
          {isRequesting ? 'Requesting...' : `Open ${settingsName}`}
        </Button>
      </div>
    </Dialog>
  );
};
